import { supabase } from "@/integrations/supabase/client";

export type InstagramPostThumbnailFields = {
  id: string;
  storage_thumbnail_object_key?: string | null;
  storage_thumbnail_url?: string | null;
  thumbnail_url?: string | null;
  media_url?: string | null;
};

/** Fallback direto (CDN Instagram) — pode expirar. */
export function instagramPostCdnFallback(
  post: Pick<
    InstagramPostThumbnailFields,
    "thumbnail_url" | "media_url"
  > | null | undefined,
): string | null {
  if (!post) return null;
  return post.thumbnail_url || post.media_url || null;
}

export function needsInstagramPostThumbnailPresign(
  post: Pick<
    InstagramPostThumbnailFields,
    "storage_thumbnail_object_key" | "storage_thumbnail_url"
  >,
): boolean {
  return Boolean(
    post.storage_thumbnail_object_key?.trim() ||
      (post.storage_thumbnail_url?.includes("/instagram-posts/") ?? false),
  );
}

/**
 * Resolve URLs assinadas para miniaturas persistidas no MinIO (bucket privado).
 * Retorna mapa postId → URL exibível.
 */
export async function resolveInstagramPostThumbnailUrls(
  workspaceId: string,
  posts: InstagramPostThumbnailFields[],
): Promise<Record<string, string>> {
  const postIds = posts
    .filter(needsInstagramPostThumbnailPresign)
    .map((post) => post.id);

  if (postIds.length === 0) return {};

  const { data, error } = await supabase.functions.invoke(
    "presign-instagram-post-thumbnail",
    { body: { workspace_id: workspaceId, post_ids: postIds } },
  );

  if (error) {
    console.warn("[instagram-post-thumbnail] presign failed:", error);
    return {};
  }

  const thumbnails = (data as {
    thumbnails?: Record<string, { url?: string }>;
  } | null)?.thumbnails ?? {};

  const resolved: Record<string, string> = {};
  for (const [postId, entry] of Object.entries(thumbnails)) {
    if (entry?.url) resolved[postId] = entry.url;
  }
  return resolved;
}

export function instagramPostDisplayThumbnail(
  post: InstagramPostThumbnailFields,
  presignedByPostId: Record<string, string>,
): string | null {
  return presignedByPostId[post.id] || instagramPostCdnFallback(post);
}
